/**
 * เลือกวิชา — ธีม "หนังสือเรียน" (D18 · mockup docs/ui-book-theme.html)
 *
 * ตัวเลขทุกตัวมาจาก `/catalog/subjects` ของจริง ไม่ใช่ค่าฝังในไฟล์
 * แถบความคืบหน้าของวิชา = ผลรอบล่าสุดของผู้เรียนคนนี้ในวิชานั้น (ไม่มี = แถบว่าง)
 * ★ สีวิชามาจาก `color_key` ของวิชา — ไม่มี key = สี brand ไม่ใช่หน้าพัง
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Center, Shell } from "@/components/Shell";
import { translate as t } from "@/i18n";
import { api } from "@/lib/api";
import { colorClass, useBookSkin } from "@/lib/book";
import { useSession } from "@/lib/session";
import type { SubjectOut } from "@/lib/types";

export function SubjectStrip({
  subjects,
  current,
}: {
  subjects: SubjectOut[];
  current?: string;
}): JSX.Element {
  const navigate = useNavigate();
  return (
    <div className="bk-subjects" data-testid="subjects">
      {subjects.map((s) => {
        const pct = s.progress?.pct ?? 0;
        return (
          <button
            key={s.id}
            type="button"
            className={`bk-subj ${colorClass(s.color_key)}`}
            data-color-key={s.color_key ?? ""}
            aria-current={current === s.code ? "true" : undefined}
            onClick={() => navigate(`/learn/${s.code}`)}
          >
            <span className="nm">{s.name_th}</span>
            <span className="ct">
              {t("subject.count", "th", {
                questions: s.published_question_count,
                topics: s.topic_count,
              })}
            </span>
            <span className="bk-bar" aria-hidden="true">
              <i style={{ width: `${pct}%` }} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function SubjectPage(): JSX.Element {
  useBookSkin();
  const navigate = useNavigate();
  const logout = useSession((st) => st.logout);
  // ★ ระดับชั้น: เด็กถูกกรองที่เซิร์ฟเวอร์เสมอ (พารามิเตอร์ถูกเมิน) — ผู้ปกครองที่มาลองทำ
  //   ส่งชั้นของลูกคนแรกไป เพื่อให้เห็นสิ่งเดียวกับที่ลูกเห็น
  const me = useSession((st) => st.me);
  const kid = me?.active_learner ?? me?.learners[0] ?? null;
  const gradeQ = me && !me.active_learner && kid ? `?grade=${kid.grade}` : "";
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["subjects", gradeQ],
    queryFn: () => api.get<SubjectOut[]>(`/catalog/subjects${gradeQ}`),
  });

  return (
    <Shell
      here={t("nav.here")}
      who={kid ? { label: kid.grade_label, name: kid.nickname } : undefined}
    >
      <h1>{t("book.title")}</h1>
      <p className="sub">
        {kid ? `${kid.grade_label} · ${t("book.pickSubject")}` : ""}
      </p>

      {isLoading ? (
        <div className="bk-subjects">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="bk-subj ck-brand skel"
              style={{ minHeight: 92 }}
            />
          ))}
        </div>
      ) : null}

      {isError ? (
        <Center>
          <p>{t("error.network")}</p>
          <button type="button" className="quit" onClick={() => void refetch()}>
            {t("error.retry")}
          </button>
        </Center>
      ) : null}

      {data ? <SubjectStrip subjects={data} /> : null}

      <p className="hint">{t("subject.hint")}</p>

      {/* ★ หน้านี้เป็นหน้าแรกของเด็ก จึงไม่มีปุ่มย้อนกลับ — แต่ต้องมีทางออก
          อยู่ดี ไม่งั้นบน iPad ที่เปิดจากไอคอนหน้าจอโฮมจะ "เข้าได้อย่างเดียว
          ออกไม่ได้" (e2e ข้อ 5.ค จับข้อนี้ได้ตอนไล่ทุก route) */}
      <button
        type="button"
        className="link"
        data-exit="signout"
        style={{ display: "block", margin: "32px auto 0", minHeight: 44 }}
        onClick={() => {
          void logout().then(() => navigate("/enter", { replace: true }));
        }}
      >
        {t("gate.signOut")}
      </button>
    </Shell>
  );
}
